import { Box, styled, Breadcrumbs, IconButton, TextField, CircularProgress } from '@mui/material';
import { ReportTable } from './ReportTable';
import { ReportInfo } from './ReportInfo';
import { ReportTypeIcon } from './ReportTypeIcon';
import { BodyText } from '@/components/BodyText';
import { Ellipsis } from '@/components/Ellipsis/index';
import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { useGetReport } from '@/hooks/api/reports/useGetReport';
import { useInfiniteReportGroups } from '@/hooks/api/reports/useInfiniteReportGroups';
import { useUpdateReportName } from '@/hooks/api/reports/useUpdateReportName';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useToast } from '@/hooks/useToast';
import { REPORT_TITLES } from './consts';
import { ReportPageObjectModel } from '@/poms/reportPageObjectModel';
import { TableChart as ReportsIcon } from '@mui/icons-material';
import { useTenants } from '@/services/tenants/useTenants';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { ReportDragProvider } from './ReportDragContext';
import { ReportMasterDetailProvider } from './ReportMasterDetailContext';

const HeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 12px 6px 12px',
  minWidth: 0,
});

const ContentBox = styled(Box)({
  flex: 1,
  display: 'flex',
  justifyContent: 'flex-start',
  overflow: 'hidden',
  margin: '0 0 6px 0',
  height: '100%',
  minHeight: 0,
  minWidth: 0,
  gap: '6px',
  padding: '6px',
});

const LeftColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  width: '270px',
  flexShrink: 0,
});

const ReportNameContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  minWidth: 0,
  '& .edit-icon': {
    opacity: 0,
    transition: 'opacity 0.2s',
  },
  '&:hover .edit-icon': {
    opacity: 1,
  },
});

const ReportNameCrumb = ({ reportId, displayName }: { reportId: string; displayName: string }) => {
  const { permissions } = useGetCurrentUser();
  const showToast = useToast(state => state.showToast);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(displayName);

  useEffect(() => {
    if (!isEditing) setEditedName(displayName);
  }, [displayName, isEditing]);

  const { mutate: updateReportName, isPending } = useUpdateReportName({
    onSuccess: () => showToast({ message: 'Report name updated successfully', type: 'success' }),
    onError: error => showToast({ message: error, type: 'error' }),
  });

  const canRename = permissions.canManageReportGroups;

  const handleSave = () => {
    const trimmed = editedName.trim();
    if (!trimmed || trimmed === displayName) {
      setIsEditing(false);
      setEditedName(displayName);
      return;
    }
    updateReportName({ id: reportId, name: trimmed });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedName(displayName);
    setIsEditing(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSave();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      handleCancel();
    }
  };

  if (isEditing || isPending) {
    return (
      <Box
        display='flex'
        alignItems='center'
        gap='4px'
        minWidth={0}
        component='form'
        onSubmit={event => {
          event.preventDefault();
          handleSave();
        }}
      >
        <TextField
          value={editedName}
          onChange={event => setEditedName(event.target.value)}
          onKeyDown={handleKeyDown}
          size='small'
          autoFocus
          disabled={isPending}
          sx={{ minWidth: '200px' }}
        />
        <IconButton size='small' onClick={handleSave} disabled={isPending} aria-label='Save report name'>
          {isPending ? <CircularProgress size={16} /> : <CheckIcon fontSize='small' />}
        </IconButton>
        <IconButton size='small' onClick={handleCancel} disabled={isPending} aria-label='Cancel rename'>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Box>
    );
  }

  return (
    <ReportNameContainer>
      <BodyText sx={{ minWidth: 0 }}>
        <Ellipsis text={displayName} />
      </BodyText>
      {canRename && (
        <IconButton
          className='edit-icon'
          size='small'
          aria-label='Rename report'
          onClick={() => setIsEditing(true)}
          sx={{ padding: '2px' }}
        >
          <EditIcon sx={{ fontSize: '14px' }} />
        </IconButton>
      )}
    </ReportNameContainer>
  );
};

const ReportHeader = () => {
  const { id, groupId } = useParams();
  const { report, isLoading: isLoadingReport } = useGetReport({ id });
  const { groups, isLoading: isLoadingGroups } = useInfiniteReportGroups({ id });
  const { getPath } = useTenants();

  const selectedGroup = groups.find(group => group.id === groupId);

  if (isLoadingReport || isLoadingGroups || !report) {
    return;
  }

  const groupName = selectedGroup?.name || '';
  const displayName = report.name || REPORT_TITLES[report.reportType];

  return (
    <HeaderRow>
      <Breadcrumbs
        data-testid={ReportPageObjectModel.ReportBreadcrumbs}
        sx={{
          color: 'text.secondary',
          flex: 1,
          minWidth: 0,
          '& .MuiBreadcrumbs-ol': {
            flexWrap: 'nowrap',
            alignItems: 'center',
          },
          '& .MuiBreadcrumbs-li': {
            overflow: 'hidden',
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiBreadcrumbs-li:first-of-type': {
            flexShrink: 0,
          },
        }}
      >
        <Link
          to={getPath('report')}
          style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <ReportsIcon sx={{ fontSize: '24px' }} />
          <BodyText>Reports</BodyText>
        </Link>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
            <ReportTypeIcon reportType={report.reportType} />
          </Box>
          <ReportNameCrumb reportId={report.id} displayName={displayName} />
        </Box>
        {groupName && (
          <BodyText sx={{ minWidth: 0 }}>
            <Ellipsis text={groupName} />
          </BodyText>
        )}
      </Breadcrumbs>
    </HeaderRow>
  );
};

export const ReportContent = () => {
  const { id: reportId, groupId } = useParams();
  const gridApiReference = useGridApiRef();

  return (
    <ReportDragProvider groupId={groupId} gridApiReference={gridApiReference}>
      <ReportMasterDetailProvider reportId={reportId} groupId={groupId}>
        <Box
          data-testid={ReportPageObjectModel.ReportContent}
          sx={{ height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}
        >
          <ReportHeader />
          <ContentBox>
            <LeftColumn>
              <ReportInfo />
            </LeftColumn>
            <ReportTable />
          </ContentBox>
        </Box>
      </ReportMasterDetailProvider>
    </ReportDragProvider>
  );
};
